import { useCallback, useEffect, useState } from 'react';

import type {
  ContentCategory,
  ContentTopic,
} from '@xing-com/crate-common-graphql-types';

import {
  MAX_CHARACTERS_TOPIC_FIELD,
  TOPICS_ARRAY_LENGTH,
} from '../../config/constants';
import type { UseYourTopicsEditViewObject } from '../../types';

const createEmptyTopicsArray = (length: number): ContentTopic[] =>
  Array.from({ length }, () => ({ name: '' }));

export const useYourTopicsEditView = (
  topicSuggestions: ContentCategory[],
  insiderTopics: ContentTopic[]
): UseYourTopicsEditViewObject => {
  const [initialTopics, setInitialTopics] = useState<ContentTopic[]>(
    createEmptyTopicsArray(TOPICS_ARRAY_LENGTH)
  );
  const [topics, setTopics] = useState<ContentTopic[]>(
    createEmptyTopicsArray(TOPICS_ARRAY_LENGTH)
  );
  const [suggestions, setSuggestions] =
    useState<ContentCategory[]>(topicSuggestions);

  useEffect(() => {
    if (insiderTopics.length) {
      const initialTopicsArray = [
        ...insiderTopics,
        ...createEmptyTopicsArray(TOPICS_ARRAY_LENGTH - insiderTopics.length),
      ];

      setTopics(initialTopicsArray);
      setInitialTopics(initialTopicsArray);
    }

    if (topicSuggestions?.length) {
      setSuggestions(topicSuggestions);
    }
  }, [topicSuggestions, insiderTopics]);

  const handleChange = useCallback(
    (value: string, index: number): void => {
      if (value.length > MAX_CHARACTERS_TOPIC_FIELD) return;

      setTopics((prevTopics) =>
        prevTopics.map((topic, i) =>
          i === index ? { ...topic, name: value } : topic
        )
      );

      if (topicSuggestions?.length) {
        const searchValue = value.toLowerCase();
        const newSuggestions = topicSuggestions.filter((category) =>
          category.name.toLowerCase().includes(searchValue)
        );
        setSuggestions(newSuggestions);
      }
    },
    [topicSuggestions, setSuggestions]
  );

  const resetSuggestions = useCallback((): void => {
    if (topicSuggestions?.length) {
      setSuggestions(topicSuggestions);
    }
  }, [topicSuggestions]);

  return {
    initialTopics,
    topics,
    suggestions,
    handleChange,
    resetSuggestions,
  };
};
